import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock, Facebook, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";

const Navbar = () => {
  const { t, lang, toggleLang } = useLang();
  const { CONTACT } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const hajjMenu = [
    { to: "/hajj/pre-registration", label: t.nav.hajj_prereg },
    { to: "/hajj/requirements", label: t.nav.hajj_req },
    { to: "/hajj", label: t.nav.hajj_packages },
  ];

  const aboutMenu = [
    { to: "/about", label: t.nav.about },
    { to: "/gallery", label: t.nav.gallery },
  ];

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/hajj", label: t.nav.hajj_menu, children: hajjMenu },
    { to: "/umrah", label: t.nav.umrah },
    { to: "/customize", label: t.nav.custom },
    { to: "/halal-tours", label: t.nav.halal },
    { to: "/blog", label: t.nav.blog },
    { to: "/about", label: t.nav.about, children: aboutMenu },
    { to: "/contact", label: t.nav.contact },
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const isExact = (to) => location.pathname === to;
  const parentActive = (l) => (l.children ? l.children.some((ch) => isActive(ch.to)) : isActive(l.to));

  return (
    <header className="sticky top-0 inset-x-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="hidden md:block mesh-band text-white/90 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-10">
          <div className="flex items-center gap-6">
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-jannah-gold-light">
              <Mail size={14} className="text-jannah-gold" /> {CONTACT.email}
            </a>
            <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 hover:text-jannah-gold-light">
              <Phone size={14} className="text-jannah-gold" /> {CONTACT.phone}
            </a>
          </div>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-2"><Clock size={14} className="text-jannah-gold" /> {(lang === "en" ? CONTACT.hours_en : CONTACT.hours_bn) || t.top_hours}</span>
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noreferrer"
              className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-jannah-gold hover:text-white"
              aria-label="Facebook"
            >
              <Facebook size={14} />
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-white/85 backdrop-blur-xl border-b border-jannah-gold/30 transition-shadow duration-300 ${scrolled ? "shadow-lg shadow-jannah-green-dark/5" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20 md:h-24">
          <Link to="/" aria-label="Fly Jannah home" data-testid="nav-logo-link">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) =>
              l.children ? (
                <div key={l.to} className="relative group" data-testid={`nav-dropdown-${l.to.slice(1)}`}>
                  <button
                    className={`px-3 py-2 text-[13px] font-semibold uppercase tracking-wide relative inline-flex items-center gap-1 transition-colors ${
                      parentActive(l) ? "text-jannah-green" : "text-jannah-ink/80 hover:text-jannah-green"
                    }`}
                    data-testid={`nav-${l.to.slice(1)}-trigger`}
                  >
                    {l.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                    {parentActive(l) && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-jannah-gold rounded" />}
                  </button>
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="w-72 rounded-2xl bg-white/95 backdrop-blur-xl shadow-2xl border border-jannah-gold/30 overflow-hidden py-2">
                      {l.children.map((ch) => (
                        <Link
                          key={ch.to}
                          to={ch.to}
                          data-testid={`nav-item-${ch.to.split("/").pop()}`}
                          className={`flex items-center justify-between px-5 py-3 text-sm font-medium border-l-[3px] transition-colors ${
                            isExact(ch.to)
                              ? "border-jannah-gold text-jannah-green bg-jannah-green/5"
                              : "border-transparent text-jannah-ink/80 hover:bg-jannah-green/5 hover:text-jannah-green hover:border-jannah-gold"
                          }`}
                        >
                          {ch.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`px-3 py-2 text-[13px] font-semibold uppercase tracking-wide relative transition-colors ${
                    isActive(l.to) ? "text-jannah-green" : "text-jannah-ink/80 hover:text-jannah-green"
                  }`}
                >
                  {l.label}
                  {isActive(l.to) && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-jannah-gold rounded" />
                  )}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="px-3.5 py-1.5 text-sm font-semibold rounded-full border border-jannah-green/40 text-jannah-green hover:bg-jannah-green hover:text-white transition-colors"
            >
              {lang === "en" ? "বাংলা" : "EN"}
            </button>
            <Link
              to="/customize"
              data-testid="nav-book-now"
              className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full btn-gold font-bold text-xs uppercase tracking-widest"
            >
              {t.book_now}
            </Link>
            <button
              className="lg:hidden text-jannah-green"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-jannah-sand shadow-lg">
          <nav className="flex flex-col px-6 py-4">
            {links.map((l) =>
              l.children ? (
                <div key={l.to} className="py-3 border-b border-jannah-sand/60">
                  <div className="text-xs font-bold uppercase tracking-widest text-jannah-green">{l.label}</div>
                  <div className="mt-2 flex flex-col gap-1 pl-3 border-l-2 border-jannah-gold/50">
                    {l.children.map((ch) => (
                      <Link key={ch.to} to={ch.to} className={`py-2 text-base font-medium ${isExact(ch.to) ? "text-jannah-green" : "text-jannah-ink/80"}`}>
                        {ch.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`py-3 border-b border-jannah-sand/60 text-base font-medium ${
                    isActive(l.to) ? "text-jannah-green" : "text-jannah-ink/80"
                  }`}
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/customize"
              className="mt-4 text-center px-5 py-3 rounded-full btn-gold font-bold uppercase tracking-widest text-sm"
            >
              {t.book_now}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
